import type { InputRef } from 'antd';
import React, { useRef, useState } from 'react';
import { Control, Controller, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import {
  AppButton,
  AppForm,
  AppFormLabel,
  AppTextField,
  AppTypography,
} from '@/components/atoms';
import AddressSearchModal from '@/components/organisms/AddressSearchModal';
import { AddressResult } from '@/hooks/useAddressSearch';
import useAppToast from '@/hooks/useAppToast';
import { fullWidthToHalfWidthNumber } from '@/utils/japaneseText';

import type { TPatientCertificationForm } from './schema';

interface AddressInfoSectionProps {
  control: Control<TPatientCertificationForm>;
  setValue: UseFormSetValue<TPatientCertificationForm>;
  watch: UseFormWatch<TPatientCertificationForm>;
  isLoading?: boolean;
}

/** Split a postal code string into (xxx, xxxx) parts, ignoring separators. */
const splitPostalCode = (raw: string) => {
  const cleaned = raw.replace(/[-−ー]/g, '');
  return {
    postalCode1: cleaned.slice(0, 3),
    postalCode2: cleaned.slice(3, 7),
  };
};

export const AddressInfoSection: React.FC<AddressInfoSectionProps> = ({
  control,
  setValue,
  watch,
  isLoading = false,
}) => {
  const toast = useAppToast();
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const address2InputRef = useRef<InputRef | null>(null);

  const postalCode = watch('post') || '';
  const { postalCode1, postalCode2 } = splitPostalCode(postalCode);

  const handleAddressSelect = (result: AddressResult) => {
    setValue('post', `${result.postal_code1}${result.postal_code2}`);
    setValue('address1', result.address);
    toast.success('住所を適用しました。');
  };

  return (
    <>
      <Controller
        name="post"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item
            label={<AppFormLabel label="郵便番号" />}
            validateStatus={error ? 'error' : undefined}
            help={error?.message}
          >
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <AppTextField
                {...field}
                status={error ? 'error' : undefined}
                disabled={isLoading}
                className="w-full sm:w-[197px]"
                placeholder="郵便番号を入力"
                maxLength={7}
              />
              <AppButton
                type="primary"
                onClick={() => setIsAddressModalOpen(true)}
                disabled={isLoading}
                className="w-full bg-[#33b5e5] hover:!bg-[#33b5e5]/80 sm:w-auto"
              >
                住所補助
              </AppButton>
              <AppFormLabel label="電話番号" />
              <Controller
                name="telephone"
                control={control}
                render={({ field: telField }) => (
                  <AppTextField
                    {...telField}
                    disabled={isLoading}
                    className="w-full sm:w-[197px]"
                    maxLength={13}
                    placeholder="入力してください"
                    onChange={e => {
                      const converted = fullWidthToHalfWidthNumber(e.target.value);
                      telField.onChange(converted.replace(/[^0-9-]/g, ''));
                    }}
                  />
                )}
              />
            </div>
          </AppForm.Item>
        )}
      />

      <AppForm.Item label={<AppFormLabel label=" " />}>
        <AppTypography.Text className="text-[#ff5b57]">
          レセプトに郵便番号を印字する場合は、必ず入力してください。
        </AppTypography.Text>
      </AppForm.Item>

      <Controller
        name="address1"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AppForm.Item
            label={<AppFormLabel label="住所1" />}
            validateStatus={error ? 'error' : undefined}
            help={error?.message}
          >
            <AppTextField
              {...field}
              status={error ? 'error' : undefined}
              disabled={isLoading}
              className="w-full sm:w-[197px]"
              maxLength={100}
            />
          </AppForm.Item>
        )}
      />

      <Controller
        name="address2"
        control={control}
        render={({ field, fieldState: { error } }) => {
          const { ref: rhfRef, ...fieldProps } = field;
          return (
            <AppForm.Item
              label={<AppFormLabel label="住所2" />}
              validateStatus={error ? 'error' : undefined}
              help={error?.message}
            >
              <AppTextField
                {...fieldProps}
                status={error ? 'error' : undefined}
                ref={instance => {
                  address2InputRef.current = instance;
                  rhfRef(instance);
                }}
                disabled={isLoading}
                className="w-full sm:w-[197px]"
                maxLength={100}
              />
            </AppForm.Item>
          );
        }}
      />

      <AppForm.Item label={<AppFormLabel label=" " />}>
        <AppTypography.Text className="text-[#ff5b57]">
          現在、住所は保険証入力画面に反映できません。
          <br />
          保険証入力画面から直接入力してください。
        </AppTypography.Text>
      </AppForm.Item>

      <AddressSearchModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        onSelect={handleAddressSelect}
        initialPostalCode1={postalCode1}
        initialPostalCode2={postalCode2}
        addressType="patient"
        title="住所補助"
        focusAfterSelectRef={address2InputRef}
      />
    </>
  );
};
